<script setup lang="ts">
import { onBeforeUnmount, ref, watch, type VNodeChild } from 'vue'
import { Loader2 } from '@lucide/vue'
import RenderContent from '../internal/RenderContent'

const props = withDefaults(defineProps<{ spinning?: boolean; delay?: number; size?: 'small' | 'default' | 'large'; tip?: string; indicator?: VNodeChild; wrapperClassName?: string }>(), { spinning: true, delay: 0, size: 'default' })
const active = ref(props.spinning && props.delay === 0)
let timer: ReturnType<typeof setTimeout> | undefined
watch([() => props.spinning, () => props.delay], ([spinning, delay]) => {
  if (timer !== undefined) clearTimeout(timer)
  timer = undefined
  if (!spinning) active.value = false
  else if (delay <= 0) active.value = true
  else { active.value = false; timer = setTimeout(() => { active.value = true }, delay) }
}, { immediate: true })
onBeforeUnmount(() => { if (timer !== undefined) clearTimeout(timer) })
</script>

<template>
  <div class="rr-spin" :class="[wrapperClassName, `rr-spin--${size}`, { 'is-nested': $slots.default, 'is-spinning': active }]" :aria-busy="active">
    <div v-if="$slots.default" class="rr-spin__content" :inert="active || undefined"><slot /></div>
    <div v-if="active" class="rr-spin__indicator" role="status" :aria-label="tip || '加载中'">
      <slot name="indicator"><RenderContent v-if="indicator" :value="indicator" /><Loader2 v-else class="rr-spin__icon" :size="size === 'small' ? 16 : size === 'large' ? 32 : 24" aria-hidden="true" /></slot>
      <span v-if="tip" class="rr-spin__tip">{{ tip }}</span>
    </div>
  </div>
</template>

<style scoped>
.rr-spin { position: relative; display: inline-block; color: var(--rr-color-primary); }
.rr-spin.is-nested { display: block; }
.rr-spin__content { min-width: 0; transition: opacity 120ms ease; color: var(--rr-color-text); }
.rr-spin.is-spinning .rr-spin__content { opacity: 0.45; user-select: none; }
.rr-spin__indicator { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--rr-space-2); padding: var(--rr-space-2); }
.rr-spin.is-nested .rr-spin__indicator { position: absolute; inset: 0; z-index: 1; }
.rr-spin__icon { animation: rr-spin-rotate 800ms linear infinite; }
.rr-spin__tip { font-size: var(--rr-font-size-base); }
@keyframes rr-spin-rotate { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .rr-spin__icon { animation: none; } .rr-spin__content { transition: none; } }
</style>
